import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { subscribeQueueDepth } from "../offline/queue";
import { drainQueue } from "../offline/withQueue";

interface OfflineIndicatorProps {
  workoutId: number;
}

export function OfflineIndicator({ workoutId }: OfflineIndicatorProps) {
  const [depth, setDepth] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    return subscribeQueueDepth((d) => setDepth(d));
  }, []);

  useEffect(() => {
    function handleOnline(): void {
      void runDrain();
    }
    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
    // runDrain captures workoutId via closure on first mount; that's the
    // only id we care about because the page unmounts on navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function runDrain(): Promise<void> {
    setSyncing(true);
    setError(null);
    try {
      const res = await drainQueue();
      if (res.failedAt) {
        setError(res.failedAt.reason);
      } else if (res.succeeded > 0) {
        await queryClient.invalidateQueries({
          queryKey: ["workoutSets", workoutId],
        });
        await queryClient.invalidateQueries({
          queryKey: ["workoutExercises", workoutId],
        });
      }
    } finally {
      setSyncing(false);
    }
  }

  if (depth === 0 && !error) return null;

  return (
    <div
      role="status"
      aria-label="offline queue"
      aria-live="polite"
      className="flex items-center gap-2 rounded-md border border-amber-700 bg-amber-950/50 px-3 py-2 text-xs text-amber-200"
    >
      <span>
        Offline · {depth} pending{error ? ` · failed: ${error}` : ""}
      </span>
      <button
        type="button"
        onClick={() => void runDrain()}
        disabled={syncing}
        className="rounded-md border border-amber-700 px-2 py-1 text-xs hover:bg-amber-950 disabled:opacity-50"
      >
        {syncing ? "Syncing…" : "Sync now"}
      </button>
    </div>
  );
}
